import axios from "axios";
import { isEmpty, omitBy, isNil, map } from "lodash";
import useSession from "Hooks/session.hook";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";
import normalize from "Utils/normalizer";
import { DefaultFilter, Pagination, Sorting } from "Types/common.types";

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface PickUpFilters extends DefaultFilter {
  orgID?: string;
  vehicleType?: string;
  driver?: string;
  orFields?: string;
}

interface DropOffFilters extends DefaultFilter {
  orgID?: string;
  driver?: string;
  orFields?: string;
}

interface BatchParcelFilters {
  customerOrgIDs?: string[];
  sortingHubID?: string;
  orFields?: string;
  destination?: string;
}

interface BoxParcelFilters {
  status?: string;
  handoverPointID?: string;
  sortingHubID?: string;
  customerOrgIDs?: string;
  orFields?: string;
}

interface TrolleyParcelFilters {
  status?: string;
  handoverPointID?: string;
  sortingHubID?: string;
  customerOrgIDs?: string;
  orFields?: string;
}

interface TrainPlanFilters {
  status?: string;
  trainLine?: string;
  orFields?: string;
}
/*
    CTRL-F the following to find their APIs (ordered as so)
    - PICKUP
    - DROPOFF
    - BATCH_PARCEL
    - BOX_PARCEL
    - TROLLEY_PARCEL
    - TRAIN_PLAN

    Each item in the above list follows the categorization in Swagger
*/

// ---- PICKUP ---- //

export const createPickUp = async (inputData: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/pickup/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createPickUp(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createCombinePickUp = async (inputData: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/pickup/combine_task/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createCombinePickUp(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchPickUp = async (pickupID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/pickup/fetch`,
      data: { pickupID: pickupID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.pickup;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchPickUp(pickupID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listPickUp = async (
  filters?: PickUpFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/pickup/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.pickups;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listPickUp(filters);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updatePickUpStatus = async (pickupID: string, status: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/pickup/status/update`,
      data: { pickupID, status },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updatePickUpStatus(pickupID, status);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateAssignDriver = async (
  inputValue,
  // pickupID: string,
  // driverID: string,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/pickup/assign_driver/update`,
      data: inputValue,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateAssignDriver(inputValue);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- DROPOFF ---- //

export const createDropOff = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/dropoff/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createDropOff(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchDropOff = async (dropoffID: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/dropoff/fetch`,
      data: { dropoffID: dropoffID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.dropoff;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchDropOff(dropoffID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listDropOff = async (
  filters?: DropOffFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/dropoff/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.dropoffs;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listPickUp(filters);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- BATCH_PARCEL ---- //
export const createBatch = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/batch_parcel/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createBatch(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchBatch = async (batchParcelID: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/batch_parcel/fetch`,
      data: { batchParcelID: batchParcelID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.batchParcel;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchBatch(batchParcelID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listBatches = async (
  filters?: BatchParcelFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/batch_parcel/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.batchParcels;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listBatches(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const addToBatch = async (batchParcelID: string, parcels: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/batch_parcel/add_to_batch/update`,
      data: {
        batchParcelID: batchParcelID,
        parcels: map(parcels, (parcel) => {
          return normalize.model.add_to_batch(parcel);
        }),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await addToBatch(batchParcelID, parcels);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error };
    }
  }
};

export const breakBatch = async (
  batchParcelID: string | undefined,
  parcelIDs: string[],
  handoverPointID: string,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/batch_parcel/break_batch/update`,
      data: {
        batchParcelID: batchParcelID,
        parcelIDs: parcelIDs,
        handoverPointID: handoverPointID,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await breakBatch(batchParcelID, parcelIDs, handoverPointID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateBatchHandoverPoint = async (payload) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/batch_parcel/assign_handoverPoint/update`,
      data: {
        batchParcelID: payload.batchParcelID,
        handoverPointID: payload.handoverPointID,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await updateBatchHandoverPoint(payload);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- BOX_PARCEL ---- //
export const createBoxParcel = async (inputData: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createBoxParcel(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateBoxParcelStatus = async (
  boxParcelID: string,
  status: string,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/status/update`,
      data: { boxParcelID: boxParcelID, status: status },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await updateBoxParcelStatus(boxParcelID, status);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const addBoxParcel = async (
  boxID: string,
  parcelTrackingNumber: string,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/add_parcels/update`,
      data: { boxID: boxID, parcelTrackingNumber: parcelTrackingNumber },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await addBoxParcel(boxID, parcelTrackingNumber);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listBoxes = async (
  filters?: BoxParcelFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.boxParcels;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listBoxes(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listMergedBoxParcels = async (
  filters?: { id?: any; type?: string },
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/merged/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listMergedBoxParcels(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listDriverBoxes = async (
  filters?: BoxParcelFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/driver/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.drivers;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listDriverBoxes(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchBox = async (boxParcelID: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/fetch`,
      data: { boxParcelID: boxParcelID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.boxParcel;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchBox(boxParcelID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const boxAssignDriver = async (payload: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/box_parcel/assign_driver/update`,
      data: omitBy({ ...payload }, isNil),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await boxAssignDriver(boxParcelIDs, assignedDriver);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ----TROLLEY_PARCEL---- //
export const createTrolleyParcel = async (inputData: any) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/trolley_parcel/create`,
      data: inputData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createTrolleyParcel(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchTrolleyParcel = async (trolleyParcelID: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/trolley_parcel/fetch`,
      data: { trolleyParcelID: trolleyParcelID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return normalize.response.trolley_details(response.data.trolleyParcel);
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrolleyParcel(trolleyParcelID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listTrolley = async (
  filters?: BoxParcelFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/trolley_parcel/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.trolleyParcels;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listTrolley(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateTrolleyParcelStatus = async (
  trolleyParcelID: string,
  status: string,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/trolley_parcel/status/update`,
      data: { trolleyParcelID: trolleyParcelID, status: status },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await updateTrolleyParcelStatus(trolleyParcelID, status);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const addItemsToTrolleyParcel = async (
  trolleyParcelID: string,
  items: string,
) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/trolley_parcel/add_items/update`,
      data: { trolleyParcelID: trolleyParcelID, items: items },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () =>
        await addItemsToTrolleyParcel(trolleyParcelID, items);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- TRAIN_PLAN ---- //
export const listTrainPlan = async (
  filters?: TrainPlanFilters,
  pagination?: Pagination,
  sorting?: Sorting,
) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/train_plan/list`,
      data: omitBy(
        {
          filters: omitBy(filters, (value) => isNil(value) || value === ""),
          pagination,
          sorting,
        },
        isNil,
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.trainPlans;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () =>
        await listTrolley(filters, pagination, sorting);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const createTrainPlan = async (inputData: any) => {
  const accessToken = getAccessToken();
  try {
    const response = await axios({
      method: "POST",
      url: `${url}/train_plan/create`,
      data: { trainPlan: inputData },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createTrainPlan(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchTrainPlan = async (trainPlanID: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/train_plan/fetch`,
      data: { trainPlanID: trainPlanID },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.trainPlan;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrainPlan(trainPlanID);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateTrainPlan = async (inputData: any) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/train_plan/update`,
      data: { trainPlan: inputData },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrainPlan(inputData);
      const { value, code, message, stack } = await errorHandler.response(
        error,
        { callback },
      );
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
